import React, { useCallback, useState } from "react";
import pxToRem from "libs/pxToRem";
import getProvinciaLabel from "libs/getProvinciaLabel";

import { DataCard, CardContainer } from "components/styled/dataCard/DataCard";
import DataCardRow from "components/styled/dataCard/DataCardRow";
import CurrencyText from "components/styled/texts/CurrencyText";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import dateToString from "libs/dateToString";

import { SolicitudConsumoAnalisisResponse } from "api/types/originacion/services/analisisSolicitudTypes";
import { EEstadoSolicitudConsumo } from "api/types/originacion/businessTypes";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useModificarPrimerVencimiento } from "components/hooks/useModificarPrimerVencimiento";
import PrimerVencimientoSelector from "./PrimerVencimientoSelector";
import { useFlags } from "launchdarkly-react-client-sdk";

const CreditoCard: React.FC<{
  operacion?: SolicitudConsumoAnalisisResponse;
  isLoading?: boolean;
}> = ({ operacion, isLoading = false }) => {
  const [editingPrimerVencimiento, setEditingPrimerVencimiento] =
    useState(false);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const { yenConsumoAprobacion } = useFlags();

  const operacionIsInValidState =
    operacion?.estado === EEstadoSolicitudConsumo.En_Analisis ||
    operacion?.estado === EEstadoSolicitudConsumo.Aprobada ||
    operacion?.estado === EEstadoSolicitudConsumo.Liquidada ||
    (operacion?.estado === EEstadoSolicitudConsumo.Desistida &&
      operacion?.fechaLiquidacion);

  const canEditPrimerVencimiento =
    (operacion?.estado === EEstadoSolicitudConsumo.En_Analisis ||
      operacion?.estado === EEstadoSolicitudConsumo.Aprobada) &&
    yenConsumoAprobacion;

  const {
    mutate: modificarPrimerVencimiento,
    isLoading: modificandoPrimerVencimiento,
  } = useModificarPrimerVencimiento(operacion?.id || 0);

  const handleEditPrimerVencimiento = useCallback(() => {
    if (operacion?.primerVencimiento) {
      setSelectedDate(operacion.primerVencimiento);
      setEditingPrimerVencimiento(true);
    }
  }, [operacion?.primerVencimiento]);

  const handleSelectDate = useCallback(
    (newDate: string) => {
      if (operacion?.id && newDate !== operacion.primerVencimiento) {
        modificarPrimerVencimiento({
          id: operacion.id,
          primerVencimiento: newDate,
        });
      }
      setEditingPrimerVencimiento(false);
    },
    [operacion?.id, operacion?.primerVencimiento, modificarPrimerVencimiento],
  );

  return (
    <CardContainer>
      <DataCard
        title="PRÉSTAMO"
        icon={<AssignmentOutlinedIcon style={{ color: "#9e9e9e" }} />}
      >
        <DataCardRow
          label="Punto de Venta"
          isLoading={isLoading}
          value={operacion?.canalNombre ? operacion.canalNombre : "-"}
          selectable
        />
        <DataCardRow
          label="Dirección (PDV)"
          isLoading={isLoading}
          value={
            operacion?.canalDomicilio || operacion?.canalCodigoPostal
              ? `${operacion.canalDomicilio} (${
                  operacion.canalCodigoPostal
                }) ${getProvinciaLabel(operacion.canalProvincia)}`
              : "-"
          }
          selectable
        />
        <DataCardRow
          label="Línea Producto"
          isLoading={isLoading}
          value={
            operacion?.lineaProducto && operacion.lineaProducto !== "0"
              ? operacion.lineaProducto
              : "-"
          }
          selectable
        />
        {operacionIsInValidState && (
          <>
            <DataCardRow
              label="Capital a Financiar"
              isLoading={isLoading}
              value={
                operacion?.importe != null ? (
                  <CurrencyText decimalScale={2} value={operacion.importe} />
                ) : (
                  "-"
                )
              }
              selectable
            />
            <DataCardRow
              label="Capital Neto"
              isLoading={isLoading}
              value={
                operacion?.importeNetoAPercibir != null ? (
                  <CurrencyText
                    decimalScale={2}
                    value={operacion.importeNetoAPercibir}
                  />
                ) : (
                  "-"
                )
              }
              selectable
            />
            <DataCardRow
              label="Sellado"
              isLoading={isLoading}
              value={
                operacion?.sellado != null ? (
                  <CurrencyText decimalScale={2} value={operacion.sellado} />
                ) : (
                  "-"
                )
              }
              selectable
            />
            <DataCardRow
              label="Plazo"
              isLoading={isLoading}
              value={operacion?.plazo ? `${operacion.plazo} cuotas` : "-"}
              selectable
            />
            <DataCardRow
              label="TNA"
              isLoading={isLoading}
              value={operacion?.tna ? `${operacion.tna}%` : "-"}
              selectable
            />
            <DataCardRow
              label="IVA"
              isLoading={isLoading}
              value={
                operacion?.planComercial?.iva != null
                  ? `${operacion.planComercial.iva * 100}%`
                  : "-"
              }
              selectable
            />
            <DataCardRow
              label="Cuota"
              isLoading={isLoading}
              value={
                operacion?.importeCuota != null ? (
                  <CurrencyText
                    decimalScale={2}
                    value={operacion.importeCuota}
                  />
                ) : (
                  "-"
                )
              }
              selectable
            />
            <DataCardRow
              label="Primer Vencimiento"
              isLoading={isLoading}
              value={
                <Box
                  style={{
                    height: pxToRem(25),
                    display: "flex",
                    alignItems: "center",
                    overflow: "visible",
                  }}
                >
                  {editingPrimerVencimiento && operacion?.primerVencimiento ? (
                    <PrimerVencimientoSelector
                      originalDate={operacion.primerVencimiento}
                      selectedDate={selectedDate}
                      onSelect={handleSelectDate}
                    />
                  ) : modificandoPrimerVencimiento ? (
                    <Box
                      style={{ textAlign: "center", maxHeight: pxToRem(25) }}
                    >
                      <CircularProgress
                        size={20}
                        style={{
                          paddingBlock: "auto",
                          marginRight: pxToRem(30),
                          marginTop: pxToRem(5),
                        }}
                      />
                    </Box>
                  ) : operacion?.primerVencimiento ? (
                    dateToString(operacion.primerVencimiento)
                  ) : (
                    "-"
                  )}
                </Box>
              }
              onEdit={
                !modificandoPrimerVencimiento &&
                operacion?.primerVencimiento &&
                canEditPrimerVencimiento
                  ? handleEditPrimerVencimiento
                  : undefined
              }
              selectable
            />
          </>
        )}
      </DataCard>
    </CardContainer>
  );
};

export default CreditoCard;
